import { useEffect, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'

import {
  listOfPangkat,
  isValidPangkat,
  getPangkatGolongan,
} from 'app/KinerjaPage/utils/pangkat'
import {
  listOfJenjang,
  isValidJenjang,
  mapJenjangName,
} from 'app/KinerjaPage/utils/jenjang'
import { capitalizeEachWords } from 'utils/text'

import type { KinerjaProfil } from 'types/kinerja'

interface EditDialogProps {
  profile: KinerjaProfil
  isOpen?: boolean
  setOpen?: (open: boolean) => void
  onSave?: (profile: KinerjaProfil) => void
}

export default function EditDialog({
  profile,
  isOpen,
  setOpen = () => {},
  onSave = () => {},
}: EditDialogProps) {
  const [profileData, setProfileData] = useState<KinerjaProfil>({ ...profile })

  const changeProfileData = (key: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [key]: value }))
  }

  const [selectedPangkatGolongan, setSelectedPangkatGolongan] =
    useState<OptionItem>({
      name: isValidPangkat(profileData.pangkatGolongan)
        ? getPangkatGolongan(profileData.pangkatGolongan.toUpperCase())
        : 'Pilih pangkat',
      value: isValidPangkat(profileData.pangkatGolongan)
        ? profileData.pangkatGolongan.toUpperCase()
        : '',
      disabled: !isValidPangkat(profileData.pangkatGolongan),
    })

  const [selectedJenjang, setSelectedJenjang] = useState<OptionItem>({
    name: isValidJenjang(profileData.jenjang)
      ? mapJenjangName(profileData.jenjang)
      : 'Pilih jenjang',
    value: isValidJenjang(profileData.jenjang)
      ? capitalizeEachWords(profileData.jenjang)
      : '',
    disabled: !isValidJenjang(profileData.jenjang),
  })

  const jenjangOption = listOfJenjang.filter((jenjang) => {
    if (profileData.kategoriPegawai === 'PPPK') {
      return jenjang !== 'Bawah Pertama'
    }

    return true
  })

  const isValid =
    isValidJenjang(selectedJenjang.value) &&
    (profileData.kategoriPegawai !== 'PNS' ||
      isValidPangkat(selectedPangkatGolongan.value))

  const userType = profileData.kategoriPegawai

  useEffect(() => {
    changeProfileData('pangkatGolongan', selectedPangkatGolongan.value)
  }, [selectedPangkatGolongan])

  useEffect(() => {
    changeProfileData('jenjang', selectedJenjang.value)
  }, [selectedJenjang])

  const handleSave = () => {
    setOpen(false)
    onSave(profileData)
  }

  return (
    <Dialog isOpen={isOpen} onClose={setOpen} size="md">
      <DialogOverlay />
      <DialogContent>
        <div className="pt-10 px-9 mb-2">
          <div className="flex flex-col gap-3 mb-5">
            <Text variant="display-sm">Edit Profil</Text>
            <div className="p-4 rounded bg-surface-warning-subdued">
              <Text as="div" variant="body">
                <Text variant="body-bold">Catatan:</Text>
                <ul className="list-disc list-outside pl-[18px]">
                  <li>
                    Pembaruan data di sini tidak akan mengubah data di Dapodik
                    dan hanya untuk keperluan Pengelolaan Kinerja
                  </li>
                  <li>
                    Silakan hubungi operator sekolah untuk pembaruan data diri
                    Anda di Dapodik
                  </li>
                </ul>
              </Text>
            </div>

            <div className="flex flex-col gap-1 h-[72px] justify-center">
              <Text variant="body">Nama</Text>
              <Text data-testid="dialog-satuan-pendidikan" variant="body-bold">
                {profileData.nama || '-'}
              </Text>
            </div>

            {userType !== 'UNDEFINED' && (
              <div className="flex flex-col gap-1 h-[72px] justify-center">
                <Text variant="body">NIP</Text>
                <Text data-testid="dialog-nip" variant="body-bold">
                  {profileData.nip || '-'}
                </Text>
              </div>
            )}

            <div className="flex flex-col gap-1 h-[72px] justify-center">
              <Text variant="body">Status Kepegawaian</Text>
              <Text data-testid="dialog-status-kepegawaian" variant="body-bold">
                {profileData.statusPegawai || '-'}
              </Text>
            </div>

            {userType === 'PNS' && (
              <div className="flex flex-col gap-1 h-[72px] justify-center">
                <Text as="label" htmlFor="pangkat" variant="body">
                  Pangkat, Golongan Ruang
                </Text>
                <CustomSelect
                  data-testid="pangkat-golongan-select"
                  value={selectedPangkatGolongan}
                  onChange={(e) => {
                    setSelectedPangkatGolongan(e as OptionItem)
                  }}
                  options={[
                    { name: 'Pilih pangkat', disabled: true, value: '' },
                    ...listOfPangkat.map((pangkat) => ({
                      name: getPangkatGolongan(pangkat),
                      value: pangkat,
                      disabled: false,
                    })),
                  ]}
                />
              </div>
            )}

            {userType !== 'UNDEFINED' && (
              <div className="flex flex-col gap-1 h-[72px] justify-center">
                <Text as="label" htmlFor="jenjang" variant="body">
                  Jenjang Jabatan
                </Text>
                <CustomSelect
                  data-testid="jenjang-select"
                  value={selectedJenjang}
                  onChange={(e) => {
                    setSelectedJenjang(e as OptionItem)
                  }}
                  options={[
                    { name: 'Pilih jenjang', disabled: true, value: '' },
                    ...jenjangOption.map((jenjang) => ({
                      name: mapJenjangName(jenjang),
                      value: jenjang,
                      disabled: false,
                    })),
                  ]}
                />
              </div>
            )}

            <div className="flex flex-col gap-1 h-[72px] justify-center">
              <Text variant="body">Satuan Pendidikan Induk</Text>
              <Text data-testid="dialog-satuan-pendidikan" variant="body-bold">
                {profileData.satuanPendidikan || '-'}
              </Text>
            </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-4">
          <Button
            size="lg"
            className="w-[155px] mr-5"
            color="white"
            onClick={() => setOpen(false)}
          >
            Keluar
          </Button>
          <Button
            disabled={!isValid}
            className="w-[155px]"
            size="lg"
            onClick={handleSave}
          >
            Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
